import React, { useState, useEffect, useCallback } from 'react';
import { Settings, BarChart3, Moon, Sun, Maximize, Minimize, RotateCcw, Copy, Download, Upload, Shuffle, Shield, Info, Hash, Key } from 'lucide-react';

interface CipherStats {
  totalEncryptions: number;
  totalDecryptions: number;
  totalOperations: number;
  totalCharactersProcessed: number;
  averageTextLength: number;
  longestText: number;
  keysUsed: string[];
  averageKeyLength: number;
  totalShifts: number;
  uniqueKeys: Set<string>;
}

interface CipherHistory {
  id: string;
  input: string;
  output: string;
  numericKey: string;
  operation: 'encrypt' | 'decrypt';
  timestamp: Date;
  textLength: number;
  keyLength: number;
  totalShifts: number;
}

const GronsfeldCipherApp: React.FC = () => {
  // Core state
  const [inputText, setInputText] = useState<string>('');
  const [outputText, setOutputText] = useState<string>('');
  const [numericKey, setNumericKey] = useState<string>('31415');
  const [operation, setOperation] = useState<'encrypt' | 'decrypt'>('encrypt');
  
  // UI state
  const [isDarkMode, setIsDarkMode] = useState<boolean>(false);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [activeModal, setActiveModal] = useState<'settings' | 'analytics' | 'help' | 'shifts' | null>(null);
  
  // Advanced options state
  const [preserveCase, setPreserveCase] = useState<boolean>(true);
  const [preserveSpaces, setPreserveSpaces] = useState<boolean>(true);
  const [preservePunctuation, setPreservePunctuation] = useState<boolean>(true);
  const [showShifts, setShowShifts] = useState<boolean>(false);
  const [groupOutput, setGroupOutput] = useState<boolean>(false);
  const [groupSize, setGroupSize] = useState<number>(5);
  const [autoKey, setAutoKey] = useState<boolean>(false);
  const [skipNonLetters, setSkipNonLetters] = useState<boolean>(true);
  
  // Analytics state
  const [stats, setStats] = useState<CipherStats>({
    totalEncryptions: 0,
    totalDecryptions: 0,
    totalOperations: 0,
    totalCharactersProcessed: 0,
    averageTextLength: 0,
    longestText: 0,
    keysUsed: [],
    averageKeyLength: 0,
    totalShifts: 0,
    uniqueKeys: new Set<string>()
  });
  const [history, setHistory] = useState<CipherHistory[]>([]);
  const [shiftPattern, setShiftPattern] = useState<number[]>([]);

  // Clean text based on preservation settings
  const cleanText = useCallback((text: string): string => {
    let cleaned = text;
    
    if (!preserveSpaces) {
      cleaned = cleaned.replace(/\s/g, '');
    }
    
    if (!preservePunctuation) {
      cleaned = cleaned.replace(/[^\w\s]/g, '');
    }
    
    if (!preserveCase) {
      cleaned = cleaned.toUpperCase();
    }
    
    return cleaned;
  }, [preserveSpaces, preservePunctuation, preserveCase]);

  // Generate extended key from numeric key
  const generateExtendedKey = useCallback((text: string, key: string): number[] => {
    if (!key || !text) return [];
    
    const numericPattern = key.split('').map(char => parseInt(char)).filter(num => !isNaN(num));
    if (numericPattern.length === 0) return [];
    
    const shifts: number[] = [];
    let keyIndex = 0;
    
    for (let i = 0; i < text.length; i++) {
      const char = text[i];
      
      if (/[A-Za-z]/.test(char)) {
        if (autoKey && shifts.length > 0) {
          // Use previous character's shift + original key pattern
          const baseShift = numericPattern[keyIndex % numericPattern.length];
          const prevShift = shifts[shifts.length - 1];
          shifts.push((baseShift + prevShift) % 10);
        } else {
          shifts.push(numericPattern[keyIndex % numericPattern.length]);
        }
        keyIndex++;
      } else if (!skipNonLetters) {
        shifts.push(0); // No shift for non-letters
      } else {
        shifts.push(-1); // Mark to skip
      }
    }
    
    return shifts;
  }, [autoKey, skipNonLetters]);

  // Apply Caesar shift to a character
  const shiftCharacter = useCallback((char: string, shift: number, encrypt: boolean): string => {
    if (!/[A-Za-z]/.test(char)) return char;
    
    const isUpperCase = char === char.toUpperCase();
    const baseCode = isUpperCase ? 65 : 97; // 'A' or 'a'
    const charCode = char.charCodeAt(0) - baseCode;
    
    let newCharCode;
    if (encrypt) {
      newCharCode = (charCode + shift) % 26;
    } else {
      newCharCode = (charCode - shift + 26) % 26;
    }
    
    return String.fromCharCode(newCharCode + baseCode);
  }, []);

  // Gronsfeld cipher encryption
  const gronsfeldEncrypt = useCallback((text: string, key: string): string => {
    if (!text || !key) return '';
    
    const shifts = generateExtendedKey(text, key);
    setShiftPattern(shifts.filter(s => s !== -1));
    
    if (shifts.length === 0) return text;
    
    let result = '';
    let shiftIndex = 0;
    
    for (let i = 0; i < text.length; i++) {
      const char = text[i];
      
      if (/[A-Za-z]/.test(char)) {
        if (shiftIndex < shifts.length && shifts[shiftIndex] !== -1) {
          result += shiftCharacter(char, shifts[shiftIndex], true);
        } else {
          result += char;
        }
        shiftIndex++;
      } else {
        result += char;
        if (!skipNonLetters) shiftIndex++;
      }
    }
    
    // Apply grouping if enabled
    if (groupOutput && result) {
      const groups = [];
      for (let i = 0; i < result.length; i += groupSize) {
        groups.push(result.slice(i, i + groupSize));
      }
      result = groups.join(' ');
    }
    
    return result;
  }, [generateExtendedKey, shiftCharacter, skipNonLetters, groupOutput, groupSize]);

  // Gronsfeld cipher decryption
  const gronsfeldDecrypt = useCallback((text: string, key: string): string => {
    if (!text || !key) return '';
    
    // Remove spaces if text was grouped
    const cleanedText = groupOutput ? text.replace(/\s/g, ' ') : text;
    
    const shifts = generateExtendedKey(cleanedText, key);
    setShiftPattern(shifts.filter(s => s !== -1));
    
    if (shifts.length === 0) return cleanedText;
    
    let result = '';
    let shiftIndex = 0;
    
    for (let i = 0; i < cleanedText.length; i++) {
      const char = cleanedText[i];
      
      if (/[A-Za-z]/.test(char)) {
        if (shiftIndex < shifts.length && shifts[shiftIndex] !== -1) {
          result += shiftCharacter(char, shifts[shiftIndex], false);
        } else {
          result += char;
        }
        shiftIndex++;
      } else {
        result += char;
        if (!skipNonLetters) shiftIndex++;
      }
    }
    
    return result;
  }, [generateExtendedKey, shiftCharacter, skipNonLetters, groupOutput]);

  // Process text - main cipher logic
  const processText = useCallback(() => {
    if (!numericKey.trim()) {
      setOutputText('');
      return '';
    }
    
    const result = operation === 'encrypt' 
      ? gronsfeldEncrypt(inputText, numericKey)
      : gronsfeldDecrypt(inputText, numericKey);
    
    setOutputText(result);
    return result;
  }, [inputText, numericKey, operation, gronsfeldEncrypt, gronsfeldDecrypt]);

  // Update analytics
  const updateAnalytics = useCallback((result: string) => {
    if (!inputText || !numericKey.trim() || !result) return;
    
    const letterCount = (inputText.match(/[A-Za-z]/g) || []).length;
    const totalShifts = shiftPattern.reduce((sum, shift) => sum + shift, 0);
    
    const newHistoryItem: CipherHistory = {
      id: Date.now().toString(),
      input: inputText,
      output: result,
      numericKey: numericKey,
      operation,
      timestamp: new Date(),
      textLength: inputText.length,
      keyLength: numericKey.length,
      totalShifts: totalShifts
    };
    
    setHistory(prev => [newHistoryItem, ...prev.slice(0, 49)]);
    
    setStats(prevStats => {
      const newUniqueKeys = new Set(prevStats.uniqueKeys);
      newUniqueKeys.add(numericKey);
      const newKeysUsed = [...prevStats.keysUsed, numericKey];
      const totalOps = prevStats.totalOperations + 1;
      const totalChars = prevStats.totalCharactersProcessed + inputText.length;
      
      return {
        ...prevStats,
        totalEncryptions: prevStats.totalEncryptions + (operation === 'encrypt' ? 1 : 0),
        totalDecryptions: prevStats.totalDecryptions + (operation === 'decrypt' ? 1 : 0),
        totalOperations: totalOps,
        totalCharactersProcessed: totalChars,
        averageTextLength: Math.round(totalChars / totalOps),
        longestText: Math.max(prevStats.longestText, inputText.length),
        keysUsed: newKeysUsed,
        averageKeyLength: Math.round(newKeysUsed.reduce((sum, k) => sum + k.length, 0) / newKeysUsed.length),
        totalShifts: prevStats.totalShifts + totalShifts,
        uniqueKeys: newUniqueKeys
      };
    });
  }, [inputText, numericKey, operation, shiftPattern]);

  // Auto-process when inputs change
  useEffect(() => {
    if (inputText && numericKey) {
      const result = processText();
      if (result) {
        updateAnalytics(result);
      }
    } else {
      setOutputText('');
      setShiftPattern([]);
    }
  }, [inputText, numericKey, operation, autoKey, skipNonLetters]);

  // Utility functions
  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      console.error('Failed to copy text');
    }
  };

  const downloadText = (text: string, filename: string) => {
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        setInputText(content);
      };
      reader.readAsText(file);
    }
  };

  const resetAll = () => {
    setInputText('');
    setOutputText('');
    setNumericKey('31415');
    setOperation('encrypt');
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const swapTexts = () => {
    const temp = inputText;
    setInputText(outputText.replace(/\s/g, ' '));
    setOutputText(temp);
    setOperation(operation === 'encrypt' ? 'decrypt' : 'encrypt');
  };

  const generateSampleText = () => {
    const samples = [
      "ATTACK AT DAWN FROM THE NORTH",
      "THE GRONSFELD CIPHER IS SECURE",
      "MEET ME AT THE SECRET LOCATION",
      "NUMERIC KEYS ARE EASY TO REMEMBER",
      "POLYALPHABETIC SUBSTITUTION WORKS",
      "MISSION ACCOMPLISHED RETURN HOME",
      "MATHEMATICAL PRECISION IN ENCRYPTION"
    ];
    const randomSample = samples[Math.floor(Math.random() * samples.length)];
    setInputText(randomSample);
  };

  const generateRandomKey = () => {
    const keyLength = Math.floor(Math.random() * 6) + 3; // 3-8 digits
    let randomKey = '';
    for (let i = 0; i < keyLength; i++) {
      randomKey += Math.floor(Math.random() * 10).toString();
    }
    setNumericKey(randomKey);
  };

  return (
    <div className={`min-h-screen transition-colors duration-300 ${
      isDarkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'
    } ${isFullscreen ? 'fixed inset-0 z-50' : ''}`}>
      
      {/* Header */}
      <header className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border-b px-6 py-4`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Hash className="text-orange-500" size={24} />
            <div>
              <h1 className="text-2xl font-bold">Gronsfeld Cipher</h1>
              <p className="text-sm opacity-60">Numeric Key Polyalphabetic Cipher (1600s)</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            {/* Shifts Viewer */}
            <button
              onClick={() => setActiveModal(activeModal === 'shifts' ? null : 'shifts')}
              className={`p-2 rounded-lg transition-colors ${
                isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
              }`}
              title="View Shift Pattern"
            >
              <Hash size={20} />
            </button>
            
            {/* Help */}
            <button
              onClick={() => setActiveModal(activeModal === 'help' ? null : 'help')}
              className={`p-2 rounded-lg transition-colors ${
                isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
              }`}
              title="Help & Information"
            >
              <Info size={20} />
            </button>
            
            {/* Advanced Options */}
            <button
              onClick={() => setActiveModal(activeModal === 'settings' ? null : 'settings')}
              className={`p-2 rounded-lg transition-colors ${
                isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
              }`}
              title="Advanced Options"
            >
              <Settings size={20} />
            </button>
            
            {/* Analytics */}
            <button
              onClick={() => setActiveModal(activeModal === 'analytics' ? null : 'analytics')}
              className={`p-2 rounded-lg transition-colors ${
                isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
              }`}
              title="Analytics"
            >
              <BarChart3 size={20} />
            </button>
            
            {/* Theme Toggle */}
            <button
              onClick={() => setIsDarkMode(!isDarkMode)}
              className={`p-2 rounded-lg transition-colors ${
                isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
              }`}
              title="Toggle Theme"
            >
              {isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
            </button>
            
            {/* Fullscreen Toggle */}
            <button
              onClick={toggleFullscreen}
              className={`p-2 rounded-lg transition-colors ${
                isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
              }`}
              title="Toggle Fullscreen"
            >
              {isFullscreen ? <Minimize size={20} /> : <Maximize size={20} />}
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 p-6">
        <div className="max-w-6xl mx-auto space-y-6">
          
          {/* Controls */}
          <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 shadow-lg`}>
            <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium mb-2">Operation</label>
                <select
                  value={operation}
                  onChange={(e) => setOperation(e.target.value as 'encrypt' | 'decrypt')}
                  className={`w-full p-2 rounded border ${
                    isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
                  }`}
                >
                  <option value="encrypt">🔒 Encrypt</option>
                  <option value="decrypt">🔓 Decrypt</option>
                </select>
              </div>
              
              <div className="md:col-span-2">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium">Numeric Key</label>
                  <button
                    onClick={generateRandomKey}
                    className={`text-xs px-2 py-1 rounded ${
                      isDarkMode ? 'bg-orange-600 hover:bg-orange-700' : 'bg-orange-500 hover:bg-orange-600'
                    } text-white transition-colors`}
                    title="Generate Random Key"
                  >
                    Random
                  </button>
                </div>
                <input
                  type="text"
                  value={numericKey}
                  onChange={(e) => setNumericKey(e.target.value.replace(/[^0-9]/g, ''))}
                  placeholder="Enter numeric key (e.g., 31415)..."
                  className={`w-full p-2 rounded border ${
                    isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
                  }`}
                />
                <div className="text-xs opacity-60 mt-1">
                  {numericKey ? `Key length: ${numericKey.length} digits` : 'Enter digits 0-9'}
                </div>
              </div>
              
              <div className="flex items-end">
                <button
                  onClick={swapTexts}
                  className={`w-full p-2 rounded border flex items-center justify-center space-x-2 ${
                    isDarkMode ? 'bg-orange-600 border-orange-500 hover:bg-orange-700 text-white' : 'bg-orange-500 border-orange-400 hover:bg-orange-600 text-white'
                  } transition-colors`}
                  title="Swap input and output"
                >
                  <Shuffle size={16} />
                  <span>Swap</span>
                </button>
              </div>
              
              <div className="flex items-end">
                <button
                  onClick={generateSampleText}
                  className={`w-full p-2 rounded border flex items-center justify-center space-x-2 ${
                    isDarkMode ? 'bg-green-600 border-green-500 hover:bg-green-700 text-white' : 'bg-green-500 border-green-400 hover:bg-green-600 text-white'
                  } transition-colors`}
                  title="Generate sample text"
                >
                  <Key size={16} />
                  <span>Sample</span>
                </button>
              </div>
              
              <div className="flex items-end">
                <button
                  onClick={resetAll}
                  className={`w-full p-2 rounded border flex items-center justify-center space-x-2 ${
                    isDarkMode ? 'bg-gray-700 border-gray-600 hover:bg-gray-600' : 'bg-gray-100 border-gray-300 hover:bg-gray-200'
                  } transition-colors`}
                >
                  <RotateCcw size={16} />
                  <span>Reset</span>
                </button>
              </div>
            </div>
            
            {/* Shift Pattern Preview */}
            {showShifts && shiftPattern.length > 0 && (
              <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'} mb-4`}>
                <h4 className="text-sm font-medium mb-3">Shift Pattern (Key: {numericKey}):</h4>
                <div className="font-mono text-sm break-all">
                  {shiftPattern.map((shift, index) => (
                    <span
                      key={index}
                      className="inline-block px-1 mr-1 mb-1 rounded text-white"
                      style={{
                        backgroundColor: `hsl(${25 + shift * 10}, 70%, ${isDarkMode ? 50 : 60}%)`
                      }}
                    >
                      {shift}
                    </span>
                  ))}
                </div>
                <div className="text-xs opacity-60 mt-2">
                  {shiftPattern.length} shifts applied | Total shift value: {shiftPattern.reduce((sum, s) => sum + s, 0)}
                </div>
              </div>
            )}
          </div>

          {/* Text Areas */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Input */}
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 shadow-lg`}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Input Text</h3>
                <div className="text-sm opacity-60">
                  {inputText.length} characters
                </div>
              </div>
              <textarea
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                placeholder="Enter text to encrypt or decrypt using Gronsfeld cipher..."
                className={`w-full h-64 p-4 rounded border resize-none ${
                  isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-300'
                }`}
              />
              <div className="mt-2 flex items-center space-x-4">
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="file"
                    accept=".txt"
                    onChange={handleFileUpload}
                    className="sr-only"
                  />
                  <Upload size={16} className="mr-1" />
                  <span className="text-sm">Upload File</span>
                </label>
                <div className="text-xs opacity-60">
                  Letters: {(inputText.match(/[A-Za-z]/g) || []).length}
                </div>
              </div>
            </div>

            {/* Output */}
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 shadow-lg`}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Output Text</h3>
                <div className="flex items-center space-x-2">
                  <div className="text-sm opacity-60">
                    {outputText.length} characters
                  </div>
                  <button
                    onClick={() => copyToClipboard(outputText)}
                    className={`p-2 rounded ${
                      isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                    } transition-colors`}
                    title="Copy to clipboard"
                  >
                    <Copy size={16} />
                  </button>
                  <button
                    onClick={() => downloadText(outputText, 'gronsfeld-output.txt')}
                    className={`p-2 rounded ${
                      isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                    } transition-colors`}
                    title="Download as file"
                  >
                    <Download size={16} />
                  </button>
                </div>
              </div>
              <textarea
                value={outputText}
                readOnly
                className={`w-full h-64 p-4 rounded border resize-none ${
                  isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-300'
                }`}
              />
            </div>
          </div>
        </div>
      </main>

      {/* Shift Pattern Modal */}
      {activeModal === 'shifts' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 max-w-4xl w-full max-h-[80vh] overflow-y-auto`}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold flex items-center space-x-2">
                <Hash className="text-orange-500" />
                <span>Shift Pattern Analysis</span>
              </h2>
              <button
                onClick={() => setActiveModal(null)}
                className={`p-2 rounded ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
              >
                ×
              </button>
            </div>
            
            <div className="space-y-6">
              {shiftPattern.length > 0 && (
                <>
                  <div>
                    <h3 className="text-lg font-semibold mb-3">Numeric Key Pattern</h3>
                    <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                      <div className="text-center mb-4">
                        <div className="text-2xl font-mono font-bold">{numericKey}</div>
                        <div className="text-sm opacity-60">Original Key ({numericKey.length} digits)</div>
                      </div>
                      <div className="grid grid-cols-5 gap-2 max-w-xs mx-auto">
                        {numericKey.split('').map((digit, index) => (
                          <div
                            key={index}
                            className={`h-12 flex items-center justify-center text-lg font-mono font-bold border-2 rounded ${
                              isDarkMode ? 'bg-orange-700 border-orange-600 text-white' : 'bg-orange-100 border-orange-300 text-orange-900'
                            }`}
                          >
                            {digit}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <h3 className="text-lg font-semibold mb-3">Applied Shift Pattern</h3>
                    <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'} max-h-64 overflow-y-auto`}>
                      <div className="font-mono text-sm">
                        {shiftPattern.map((shift, index) => (
                          <span
                            key={index}
                            className="inline-block px-2 py-1 mr-1 mb-1 rounded border text-white"
                            style={{
                              backgroundColor: `hsl(${25 + shift * 15}, 70%, ${isDarkMode ? 50 : 60}%)`,
                              borderColor: `hsl(${25 + shift * 15}, 70%, ${isDarkMode ? 40 : 50}%)`
                            }}
                          >
                            {shift}
                          </span>
                        ))}
                      </div>
                      <div className="text-xs opacity-60 mt-3">
                        <div>Pattern repeats every {numericKey.length} letters</div>
                        <div>Total shifts applied: {shiftPattern.length}</div>
                        <div>Sum of all shifts: {shiftPattern.reduce((sum, s) => sum + s, 0)}</div>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <h3 className="text-lg font-semibold mb-3">Shift Statistics</h3>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                      {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map(digit => {
                        const count = shiftPattern.filter(s => s === digit).length;
                        const percentage = shiftPattern.length > 0 ? (count / shiftPattern.length * 100).toFixed(1) : '0';
                        return (
                          <div key={digit} className={`p-3 rounded border text-center ${
                            isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                          }`}>
                            <div className="text-lg font-bold">{digit}</div>
                            <div className="text-sm">{count} times</div>
                            <div className="text-xs opacity-60">{percentage}%</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </>
              )}
              
              <div>
                <h3 className="text-lg font-semibold mb-3">How Gronsfeld Works</h3>
                <div className="text-sm space-y-2">
                  <div>1. Each digit in the numeric key represents a Caesar shift (0-9)</div>
                  <div>2. The key repeats cyclically for the length of the text</div>
                  <div>3. Each letter is shifted by the corresponding key digit</div>
                  <div>4. Non-letters are preserved or skipped based on settings</div>
                  <div>5. Auto-key mode adds previous shifts to the pattern</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Help Modal */}
      {activeModal === 'help' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto`}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold flex items-center space-x-2">
                <Shield className="text-orange-500" />
                <span>About Gronsfeld Cipher</span>
              </h2>
              <button
                onClick={() => setActiveModal(null)}
                className={`p-2 rounded ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
              >
                ×
              </button>
            </div>
            
            <div className="space-y-4 text-sm">
              <div>
                <h3 className="font-semibold mb-2">What is the Gronsfeld Cipher?</h3>
                <p>The Gronsfeld Cipher is a variant of the Vigenère cipher that uses numeric keys instead of alphabetic ones. Named after Jos Gronsfeld, it applies Caesar shifts based on digits (0-9) rather than letters, making it easier to remember and use while maintaining the security benefits of polyalphabetic substitution.</p>
              </div>
              
              <div>
                <h3 className="font-semibold mb-2">How it Works</h3>
                <ol className="list-decimal list-inside space-y-1">
                  <li>Choose a numeric key (e.g., 31415)</li>
                  <li>Each digit represents a Caesar shift (0-9)</li>
                  <li>Apply shifts cyclically to each letter</li>
                  <li>Repeat the key pattern for the entire message</li>
                  <li>Non-letters can be preserved or skipped</li>
                  <li>Decryption reverses the shift process</li>
                </ol>
              </div>
              
              <div>
                <h3 className="font-semibold mb-2">Example Encryption</h3>
                <div className={`p-3 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="font-mono text-xs space-y-1">
                    <div><strong>Plaintext:</strong> HELLO</div>
                    <div><strong>Numeric Key:</strong> 31415</div>
                    <div><strong>Key Pattern:</strong> 3 1 4 1 5</div>
                    <div className="mt-2"><strong>Letter by Letter:</strong></div>
                    <div>H + 3 = K</div>
                    <div>E + 1 = F</div>
                    <div>L + 4 = P</div>
                    <div>L + 1 = M</div>
                    <div>O + 5 = T</div>
                    <div><strong>Ciphertext:</strong> KFPMT</div>
                  </div>
                </div>
              </div>
              
              <div>
                <h3 className="font-semibold mb-2">Historical Background</h3>
                <ul className="space-y-1">
                  <li><strong>Creator:</strong> Jos Gronsfeld (Dutch cryptographer)</li>
                  <li><strong>Era:</strong> 17th century (1600s)</li>
                  <li><strong>Type:</strong> Polyalphabetic substitution cipher</li>
                  <li><strong>Innovation:</strong> Numeric keys instead of alphabetic</li>
                  <li><strong>Usage:</strong> Diplomatic and commercial communications</li>
                </ul>
              </div>
              
              <div>
                <h3 className="font-semibold mb-2">Security Level</h3>
                <p className="text-orange-600">⚠️ <strong>Historical Security:</strong> The Gronsfeld cipher provided excellent security for its time, being much stronger than monoalphabetic ciphers while remaining practical to use. However, like other classical polyalphabetic ciphers, it can be broken by modern cryptanalytic methods and computer analysis. It's excellent for education and understanding polyalphabetic principles but not suitable for actual security needs.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Settings Modal */}
      {activeModal === 'settings' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 max-w-md w-full max-h-[80vh] overflow-y-auto`}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold">Advanced Options</h2>
              <button
                onClick={() => setActiveModal(null)}
                className={`p-2 rounded ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
              >
                ×
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium">Preserve Case</label>
                <input
                  type="checkbox"
                  checked={preserveCase}
                  onChange={(e) => setPreserveCase(e.target.checked)}
                  className="rounded"
                />
              </div>
              
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium">Preserve Spaces</label>
                <input
                  type="checkbox"
                  checked={preserveSpaces}
                  onChange={(e) => setPreserveSpaces(e.target.checked)}
                  className="rounded"
                />
              </div>
              
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium">Preserve Punctuation</label>
                <input
                  type="checkbox"
                  checked={preservePunctuation}
                  onChange={(e) => setPreservePunctuation(e.target.checked)}
                  className="rounded"
                />
              </div>
              
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium">Show Shift Pattern</label>
                <input
                  type="checkbox"
                  checked={showShifts}
                  onChange={(e) => setShowShifts(e.target.checked)}
                  className="rounded"
                />
              </div>
              
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium">Skip Non-Letters</label>
                <input
                  type="checkbox"
                  checked={skipNonLetters}
                  onChange={(e) => setSkipNonLetters(e.target.checked)}
                  className="rounded"
                />
              </div>
              
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium">Auto-Key Mode</label>
                <input
                  type="checkbox"
                  checked={autoKey}
                  onChange={(e) => setAutoKey(e.target.checked)}
                  className="rounded"
                />
              </div>
              
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium">Group Output</label>
                <input
                  type="checkbox"
                  checked={groupOutput}
                  onChange={(e) => setGroupOutput(e.target.checked)}
                  className="rounded"
                />
              </div>
              
              {groupOutput && (
                <div>
                  <label className="block text-sm font-medium mb-2">Group Size</label>
                  <input
                    type="range"
                    min="3"
                    max="10"
                    value={groupSize}
                    onChange={(e) => setGroupSize(parseInt(e.target.value))}
                    className="w-full"
                  />
                  <div className="text-xs opacity-60 mt-1">
                    Characters per group: {groupSize}
                  </div>
                </div>
              )}
              
              <div className="pt-4 border-t">
                <h3 className="text-sm font-medium mb-3">Common Numeric Keys</h3>
                <div className="grid grid-cols-2 gap-2">
                  {['31415', '27182', '16180', '12345', '54321', '24681'].map(key => (
                    <button
                      key={key}
                      onClick={() => setNumericKey(key)}
                      className={`p-2 text-xs rounded border ${
                        numericKey === key
                          ? (isDarkMode ? 'bg-orange-600 border-orange-500 text-white' : 'bg-orange-500 border-orange-400 text-white')
                          : (isDarkMode ? 'bg-gray-700 border-gray-600 hover:bg-gray-600' : 'bg-gray-100 border-gray-300 hover:bg-gray-200')
                      } transition-colors`}
                    >
                      {key}
                    </button>
                  ))}
                </div>
                <div className="text-xs opacity-60 mt-2">
                  π, e, φ (golden ratio), and simple patterns
                </div>
              </div>
              
              <div className="pt-4 border-t">
                <h3 className="text-sm font-medium mb-3">Historical Presets</h3>
                <div className="space-y-2">
                  <button
                    onClick={() => {
                      setNumericKey('31415');
                      setPreserveCase(true);
                      setPreserveSpaces(true);
                      setPreservePunctuation(true);
                      setSkipNonLetters(true);
                      setAutoKey(false);
                      setGroupOutput(false);
                      setShowShifts(true);
                    }}
                    className={`w-full p-2 text-sm rounded border ${
                      isDarkMode ? 'bg-orange-600 border-orange-500 hover:bg-orange-700 text-white' : 'bg-orange-500 border-orange-400 hover:bg-orange-600 text-white'
                    } transition-colors`}
                  >
                    Classical Standard
                  </button>
                  
                  <button
                    onClick={() => {
                      setNumericKey('27182');
                      setPreserveCase(false);
                      setPreserveSpaces(false);
                      setPreservePunctuation(false);
                      setSkipNonLetters(false);
                      setAutoKey(true);
                      setGroupOutput(true);
                      setGroupSize(5);
                      setShowShifts(true);
                    }}
                    className={`w-full p-2 text-sm rounded border ${
                      isDarkMode ? 'bg-amber-600 border-amber-500 hover:bg-amber-700 text-white' : 'bg-amber-500 border-amber-400 hover:bg-amber-600 text-white'
                    } transition-colors`}
                  >
                    Auto-Key Variant
                  </button>
                  
                  <button
                    onClick={() => {
                      setNumericKey('16180');
                      setPreserveCase(true);
                      setPreserveSpaces(true);
                      setPreservePunctuation(true);
                      setSkipNonLetters(true);
                      setAutoKey(false);
                      setGroupOutput(false);
                      setShowShifts(false);
                    }}
                    className={`w-full p-2 text-sm rounded border ${
                      isDarkMode ? 'bg-yellow-600 border-yellow-500 hover:bg-yellow-700 text-white' : 'bg-yellow-500 border-yellow-400 hover:bg-yellow-600 text-white'
                    } transition-colors`}
                  >
                    Modern Educational
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Analytics Modal */}
      {activeModal === 'analytics' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 max-w-3xl w-full max-h-[80vh] overflow-y-auto`}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold">Analytics Dashboard</h2>
              <button
                onClick={() => setActiveModal(null)}
                className={`p-2 rounded ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
              >
                ×
              </button>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                <div className="text-2xl font-bold text-green-500">{stats.totalEncryptions}</div>
                <div className="text-sm">Total Encryptions</div>
              </div>
              <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                <div className="text-2xl font-bold text-blue-500">{stats.totalDecryptions}</div>
                <div className="text-sm">Total Decryptions</div>
              </div>
              <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                <div className="text-2xl font-bold text-purple-500">{stats.totalShifts}</div>
                <div className="text-sm">Total Shifts Applied</div>
              </div>
              <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                <div className="text-2xl font-bold text-orange-500">{stats.totalCharactersProcessed}</div>
                <div className="text-sm">Characters Processed</div>
              </div>
              <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                <div className="text-2xl font-bold text-pink-500">{stats.uniqueKeys.size}</div>
                <div className="text-sm">Unique Keys Used</div>
              </div>
              <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                <div className="text-2xl font-bold text-cyan-500">{stats.averageKeyLength}</div>
                <div className="text-sm">Avg Key Length</div>
              </div>
              <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                <div className="text-2xl font-bold text-amber-500">{stats.averageTextLength}</div>
                <div className="text-sm">Avg Text Length</div>
              </div>
              <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                <div className="text-2xl font-bold text-red-500">{stats.longestText}</div>
                <div className="text-sm">Longest Text</div>
              </div>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold mb-4">Recent Operations</h3>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {history.length > 0 ? history.slice(0, 15).map(item => (
                  <div key={item.id} className={`p-3 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center space-x-2">
                        <span className={`text-xs px-2 py-1 rounded ${
                          item.operation === 'encrypt' ? 'bg-green-500 text-white' : 'bg-blue-500 text-white'
                        }`}>
                          {item.operation.toUpperCase()}
                        </span>
                        <span className="text-xs px-2 py-1 rounded bg-orange-500 text-white">
                          Key: {item.numericKey}
                        </span>
                        <span className="text-xs px-2 py-1 rounded bg-purple-500 text-white">
                          {item.totalShifts} shifts
                        </span>
                      </div>
                      <span className="text-xs opacity-60">
                        {item.timestamp.toLocaleTimeString()}
                      </span>
                    </div>
                    <div className="text-sm">
                      <div className="truncate font-mono">{item.input}</div>
                      <div className="truncate opacity-60 font-mono">→ {item.output}</div>
                    </div>
                  </div>
                )) : (
                  <div className="text-center py-8 opacity-60">
                    <Hash size={32} className="mx-auto mb-2" />
                    <div>No operations performed yet</div>
                    <div className="text-xs">Start encrypting or decrypting to see analytics</div>
                  </div>
                )}
              </div>
            </div>
            
            {history.length > 0 && (
              <div className="mt-6 pt-4 border-t">
                <h3 className="text-sm font-medium mb-2">Cipher Statistics</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
                  <div>
                    <div className="text-purple-500">Most Used Key:</div>
                    <div>
                      {history.length > 0 
                        ? (() => {
                            const keyCounts = history.reduce((acc, item) => {
                              acc[item.numericKey] = (acc[item.numericKey] || 0) + 1;
                              return acc;
                            }, {} as Record<string, number>);
                            return Object.keys(keyCounts).reduce((a, b) => keyCounts[a] > keyCounts[b] ? a : b);
                          })()
                        : 'N/A'
                      }
                    </div>
                  </div>
                  <div>
                    <div className="text-green-500">Encrypt/Decrypt Ratio:</div>
                    <div>{stats.totalOperations > 0 ? Math.round((stats.totalEncryptions / stats.totalOperations) * 100) : 0}% Encrypt</div>
                  </div>
                  <div>
                    <div className="text-blue-500">Avg Shifts per Operation:</div>
                    <div>{stats.totalOperations > 0 ? Math.round(stats.totalShifts / stats.totalOperations) : 0}</div>
                  </div>
                  <div>
                    <div className="text-orange-500">Mathematical Efficiency:</div>
                    <div>{stats.totalCharactersProcessed > 0 ? Math.round((stats.totalShifts / stats.totalCharactersProcessed) * 100) : 0}% Shifted</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default GronsfeldCipherApp;